// ============================================================
// Rule Lines Inventory
// Pure helpers that resolve template-scope rules into a list of
// cross-form line descriptors. Consumed by both
// RuleConnectionLines.tsx (SVG overlay) and RuleLinesLegend.tsx
// (slide-in legend panel) so the numbering + ordering stays
// in sync between the two surfaces.
// ============================================================
import type { TemplateBuilderTemplate, TemplateBuilderForm, Rule, Condition, Action } from '../../types/templateTypes';

export interface RuleLineInfo {
  ruleId: string;
  ruleName?: string;
  index: number; // 1-based numbering used by badge + legend
  sourceFormId: string;
  targetFormId: string;
  isHide: boolean; // true = hide action, false = show/require/etc.
  color: string;
}

const HIDE_COLOR = '#ef4444';
const SHOW_COLOR = '#22c55e';

/** Walk a Condition and collect every `field` referenced on a leaf. */
function collectFieldIds(cond: Condition, out: Set<string>): void {
  if (!cond) return;
  if ('and' in cond) { cond.and.forEach((c) => collectFieldIds(c, out)); return; }
  if ('or' in cond) { cond.or.forEach((c) => collectFieldIds(c, out)); return; }
  if ('not' in cond) { collectFieldIds(cond.not, out); return; }
  if ('field' in cond && typeof cond.field === 'string') { out.add(cond.field); return; }
}

/** Build fieldId -> formId index across all forms attached to the template. */
export function buildFieldToFormMap(template: TemplateBuilderTemplate, forms: Map<string, TemplateBuilderForm>): Map<string, string> {
  const map = new Map<string, string>();
  for (const ref of template.formRefs ?? []) {
    const form = forms.get(ref.formId);
    if (!form) continue;
    for (const field of form.fields ?? []) map.set(field.id, ref.formId);
  }
  return map;
}

/** Build sectionId -> formId index across all forms attached to the template. */
export function buildSectionToFormMap(template: TemplateBuilderTemplate, forms: Map<string, TemplateBuilderForm>): Map<string, string> {
  const map = new Map<string, string>();
  for (const ref of template.formRefs ?? []) {
    const form = forms.get(ref.formId);
    if (!form) continue;
    for (const section of form.layout?.sections ?? []) map.set(section.id, ref.formId);
  }
  return map;
}

/** Resolve the target formId for a single Action (or null if unresolvable / skipped). */
function resolveTargetFormId(
  action: Action,
  fieldToForm: Map<string, string>,
  sectionToForm: Map<string, string>,
): string | null {
  switch (action.action) {
    case 'hideForm':
    case 'showForm':
      return action.formId ?? null;
    case 'hideSection':
    case 'showSection':
      return sectionToForm.get(action.sectionId) ?? null;
    case 'hideField':
    case 'showField':
    case 'require':
      return fieldToForm.get(action.fieldId) ?? null;
    case 'setOptions':
      return null; // intentionally skipped from line visualization
    default:
      return null;
  }
}

/** Resolve the source formId for a rule by walking its condition and
 *  picking the first referenced field that maps to a known form. */
function resolveSourceFormId(rule: Rule, fieldToForm: Map<string, string>): string | null {
  const refs = new Set<string>();
  collectFieldIds(rule.when, refs);
  for (const fieldId of refs) {
    const formId = fieldToForm.get(fieldId);
    if (formId) return formId;
  }
  return null;
}

/**
 * Build the canonical list of cross-form rule lines.
 * Rules whose source and target resolve to the same form are intentionally
 * excluded from the returned inventory (tracked separately via
 * {@link computeInternalRuleCounts}).
 */
export function buildRuleLineInventory(
  template: TemplateBuilderTemplate,
  forms: Map<string, TemplateBuilderForm>,
): RuleLineInfo[] {
  const rules = template.rules ?? [];
  if (rules.length === 0) return [];
  const fieldToForm = buildFieldToFormMap(template, forms);
  const sectionToForm = buildSectionToFormMap(template, forms);

  const lines: RuleLineInfo[] = [];
  for (const rule of rules) {
    const sourceFormId = resolveSourceFormId(rule, fieldToForm);
    if (!sourceFormId) continue;

    for (const action of rule.then ?? []) {
      const targetFormId = resolveTargetFormId(action as Action, fieldToForm, sectionToForm);
      if (!targetFormId) continue;
      if (targetFormId === sourceFormId) continue; // self-form → counted separately
      const isHide = action.action === 'hideField' || action.action === 'hideSection' || action.action === 'hideForm';
      lines.push({
        ruleId: rule.id,
        ruleName: rule.name,
        index: lines.length + 1,
        sourceFormId,
        targetFormId,
        isHide,
        color: isHide ? HIDE_COLOR : SHOW_COLOR,
      });
    }
  }
  return lines;
}

/** Count rules whose source + at least one action target resolve to the same form. */
export function computeInternalRuleCounts(
  template: TemplateBuilderTemplate,
  forms: Map<string, TemplateBuilderForm>,
): Map<string, number> {
  const counts = new Map<string, number>();
  const rules = template.rules ?? [];
  if (rules.length === 0) return counts;
  const fieldToForm = buildFieldToFormMap(template, forms);
  const sectionToForm = buildSectionToFormMap(template, forms);

  for (const rule of rules) {
    const sourceFormId = resolveSourceFormId(rule, fieldToForm);
    if (!sourceFormId) continue;
    let selfHit = false;
    for (const action of rule.then ?? []) {
      const targetFormId = resolveTargetFormId(action as Action, fieldToForm, sectionToForm);
      if (targetFormId && targetFormId === sourceFormId) { selfHit = true; break; }
    }
    if (selfHit) counts.set(sourceFormId, (counts.get(sourceFormId) ?? 0) + 1);
  }
  return counts;
}
